import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  IconButton,
  Pagination,
  Typography,
  Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Cancel';

/**
 * Searchable, paginated product picker.
 * - Searches by name / SKU / category / brand on the server.
 * - Click a row to add its SKU to the selection.
 * - Selected SKUs render as removable chips above the search box.
 *
 * Props:
 *   value: comma-separated SKU string (or empty)
 *   onChange: (newCsvString) => void
 */
const ProductSkuPicker = ({ value, onChange }) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const selected = (value || '').split(/[\s,]+/).filter(Boolean);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let abort = false;
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const url = `https://api.elnaf.fi/api/allProdAdmin.php?page=${page}&q=${encodeURIComponent(debouncedSearch)}&lang=en`;
        const res = await fetch(url);
        const data = await res.json();
        if (abort) return;
        setProducts(Array.isArray(data.products) ? data.products : []);
        setTotalPages(parseInt(data.totalPages || 1, 10));
      } catch (e) {
        if (!abort) {
          setProducts([]);
          setTotalPages(1);
        }
      } finally {
        if (!abort) setLoading(false);
      }
    };
    fetchProducts();
    return () => { abort = true; };
  }, [debouncedSearch, page]);

  const addSku = (sku) => {
    if (!sku || selected.includes(sku)) return;
    onChange([...selected, sku].join(','));
  };

  const removeSku = (sku) => {
    onChange(selected.filter((s) => s !== sku).join(','));
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
          Selected products ({selected.length})
        </Typography>
        {selected.length > 0 && (
          <Typography
            component="button"
            type="button"
            onClick={() => onChange('')}
            sx={{
              fontSize: 12,
              color: '#ef4444',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              p: 0,
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Clear all
          </Typography>
        )}
      </Stack>

      {selected.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            p: 1.5,
            border: '1px solid #e5e7eb',
            borderRadius: 1,
            backgroundColor: '#fafafa',
            mb: 2,
            maxHeight: 120,
            overflowY: 'auto'
          }}
        >
          {selected.map((sku) => (
            <Chip
              key={sku}
              label={sku}
              onDelete={() => removeSku(sku)}
              size="small"
              sx={{
                backgroundColor: '#ede9fe',
                color: '#5b21b6',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': { color: '#7c3aed' }
              }}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            p: 1.5,
            border: '1px dashed #d1d5db',
            borderRadius: 1,
            backgroundColor: '#fafafa',
            mb: 2,
            color: '#9ca3af',
            fontSize: 12,
            textAlign: 'center'
          }}
        >
          No specific products selected — rule will apply to ALL products matching the other filters.
        </Box>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder="Search by name, SKU, brand, category..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: loading && (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          )
        }}
        sx={{ mb: 1.5 }}
      />

      <Box
        sx={{
          border: '1px solid #e5e7eb',
          borderRadius: 1,
          maxHeight: 320,
          overflowY: 'auto',
          backgroundColor: 'white'
        }}
      >
        {loading && products.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
            <CircularProgress size={24} />
          </Box>
        ) : products.length === 0 ? (
          <Box textAlign="center" py={3} color="text.secondary">
            <Typography variant="caption">No products found</Typography>
          </Box>
        ) : (
          products.map((p) => {
            const isSelected = selected.includes(p.SKU);
            return (
              <Box
                key={p.SKU}
                onClick={() => !isSelected && addSku(p.SKU)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  borderBottom: '1px solid #f3f4f6',
                  cursor: isSelected ? 'default' : 'pointer',
                  opacity: isSelected ? 0.5 : 1,
                  '&:hover': { backgroundColor: isSelected ? 'transparent' : '#f9fafb' }
                }}
              >
                {p.Foto && (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      backgroundColor: '#f3f4f6',
                      borderRadius: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    <img
                      src={p.Foto}
                      alt={p.SKU}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </Box>
                )}
                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.name}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                      SKU: {p.SKU}
                    </Typography>
                    {p.MARKA && (
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        · {p.MARKA}
                      </Typography>
                    )}
                    {p.Category && (
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        · {p.Category}
                      </Typography>
                    )}
                  </Stack>
                </Box>
                <IconButton size="small" disabled={isSelected} color="primary">
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })
        )}
      </Box>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={1.5}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            size="small"
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default ProductSkuPicker;
